import { Card, Text, Title, Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell, Badge } from '@tremor/react';
import { formatInt, formatRelativeDay } from '../lib/format';
import { severity } from '../lib/severity';
import { t } from '../lib/i18n';
import { usePagination } from '../lib/usePagination';
import Pagination from './Pagination';
import type { LowStockItem, Store } from '../api/types';
import DrillCell from './DrillCell';

interface Props {
  rows: LowStockItem[];
  stores: Store[];
}

export default function LowStockList({ rows, stores }: Props) {
  const storeName = (sid: number) => stores.find((s) => s.sid === sid)?.name ?? `#${sid}`;
  const pg = usePagination(rows);
  return (
    <Card>
      <Title>{t.tables.lowStockTitle}</Title>
      {rows.length === 0 ? (
        <Text className="mt-4">{t.tables.emptyLowStock}</Text>
      ) : (
        <div
          className="mt-4 overflow-auto print-expand border border-tremor-border rounded-tremor-default"
          style={{ maxHeight: '50vh' }}
        >
          <Table>
            <TableHead className="sticky top-0 bg-tremor-background z-10 shadow-sm">
              <TableRow>
                <TableHeaderCell>{t.tables.cols.part}</TableHeaderCell>
                <TableHeaderCell>{t.tables.cols.description}</TableHeaderCell>
                <TableHeaderCell>{t.tables.cols.store}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.tables.cols.onHand}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.tables.cols.min}</TableHeaderCell>
                <TableHeaderCell>{t.tables.cols.lastSold}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pg.pageRows.map((r) => (
                <TableRow key={`${r.line}-${r.part}-${r.sid}`}>
                  <TableCell className="font-mono text-xs">
                    <DrillCell target={{ kind: 'inv', sid: r.sid, line: r.line, part: r.part }}>
                      {r.line} {r.part}
                    </DrillCell>
                  </TableCell>
                  <TableCell>{r.pdesc}</TableCell>
                  <TableCell>{storeName(r.sid)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    <Badge color={severity(r.on_hand, r.min)}>{formatInt(r.on_hand)}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatInt(r.min)}</TableCell>
                  <TableCell title={r.last_sold ?? undefined}>
                    {formatRelativeDay(r.last_sold)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Pagination {...pg} />
    </Card>
  );
}
